/**
 * <LiveHelpSheet> — « Un problème ? » pendant un live (template).
 *
 * Le parcours d'aujourd'hui (`TroubleshootingPanel`, puis `LiveHelpModal`), en trois étapes
 * dans une même feuille (`<Modal>` : une feuille basse sur un téléphone, une carte au milieu sur
 * un grand écran) :
 *  - `choose` : « Sélectionne le problème que tu rencontres… », les cinq problèmes d'aujourd'hui ;
 *  - `diagnostic` : ce que l'écran a vérifié et ce qu'on peut essayer (le mode Concentré, qui coupe
 *    le son, se dit d'abord ; les gestes propres au diagnostic, `diagnostic.actions`, sous les
 *    étapes), puis « Toujours un problème ? » : lever la main, écrire au tuteur,
 *    signaler le problème. Une liste qui OFFRE ces gestes, pas trois boutons ;
 *  - `report` : « Signaler un problème », sa catégorie (une seule), sa description, l'envoi.
 *
 * Le retour remonte d'une étape. Contrôlé : l'étape, le problème choisi, ce que l'écran a vérifié
 * (le niveau du micro, les autorisations, la qualité de la connexion : `diagnostic`) et le
 * signalement arrivent en props. Les captures jointes au signalement (trois images au plus, la
 * galerie sur mobile, le choix de fichier et le glisser-déposer sur le web) passent par
 * `<MediaPicker>` (`report.media`), dont l'écran injecte les effets.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { type IconName } from "../../atoms/icons/Icon"
import { TextInput } from "../../atoms/TextInput"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { CardList } from "../../molecules/CardList"
import { Modal } from "../../molecules/Modal"
import { SettingRow } from "../../molecules/SettingRow"
import { MediaPicker, type MediaPickerProps } from "../../organisms/MediaPicker"
import { type UILiveProblem, type UILiveReportCategory } from "../../../ports/primary/ui/types/studentLive"

// Les problèmes et les catégories d'un signalement : leurs formes vivent dans `ports/primary/ui/types`.
export type { UILiveProblem, UILiveReportCategory }

export type LiveHelpStep = "choose" | "diagnostic" | "report"

/** Ce que l'écran a vérifié pour ce problème, et ce qu'on peut essayer. */
export type UILiveDiagnostic = {
    /** Ce qui se dit d'abord : le mode Concentré, une autorisation refusée, une connexion instable. */
    notice?: string
    steps: { iconName: IconName; text: string; detail?: string }[]
    /**
     * Les gestes propres à ce diagnostic, sous ses étapes dans « À essayer » : « J'ai autorisé le micro »
     * après un refus, « Changer de micro » (l'ancien `TroubleshootingPanel` : `PermissionAction`,
     * `DeviceChangeButton`). Une ligne qui se touche chacun ; `detail` dit pourquoi, sous son libellé.
     * Absent ou vide (défaut) : aucune ligne.
     */
    actions?: { iconName: IconName; label: string; detail?: string; onPress: () => void }[]
}

export type LiveHelpReport = {
    category: UILiveReportCategory | null
    onSelectCategory: (category: UILiveReportCategory) => void
    description: string
    onChangeDescription: (text: string) => void
    onSend: () => void
    /** L'envoi part. Défaut `false`. */
    sending?: boolean
    /** Les captures jointes (`<MediaPicker>`, effets injectés par l'écran). Absent : pas de pièce jointe. */
    media?: MediaPickerProps
    /** L'envoi a été refusé : sa phrase, au-dessus du bouton. Absent : rien. */
    error?: string | null
}

export type LiveHelpSheetProps = {
    visible: boolean
    onClose: () => void
    step: LiveHelpStep
    /** Le problème choisi, à l'étape `diagnostic`. */
    problem?: UILiveProblem | null
    onSelectProblem: (problem: UILiveProblem) => void
    /** Le retour d'une étape. */
    onBack: () => void
    diagnostic?: UILiveDiagnostic
    /**
     * Les lignes de « Toujours un problème ? » : chacune se pose quand son geste est donné — lever la
     * main et écrire au masteur (l'élève), rafraîchir la page et ouvrir les paramètres (le masteur,
     * l'ancien `TroubleshootingPanel role="tutor"`). « Signaler un problème » est toujours là.
     */
    onRaiseHand?: () => void
    onOpenChat?: () => void
    onRefresh?: () => void
    onOpenSettings?: () => void
    /** Qui a un problème : il choisit les problèmes proposés (« Les élèves ne m'entendent pas »…). Défaut `student`. */
    viewer?: "student" | "tutor"
    /** « Signaler un problème » : l'étape `report`. */
    onReport: () => void
    report: LiveHelpReport
    testID?: string
}

const PROBLEMS: { key: UILiveProblem; icon: IconName; label: string }[] = [
    { key: "mic", icon: "microphone-slash", label: "On ne m'entend pas" },
    { key: "cant_hear", icon: "volume-mute", label: "Je n'entends pas mon masteur" },
    { key: "camera", icon: "video-slash", label: "Ma caméra ne marche pas" },
    { key: "connection", icon: "wifi", label: "Ça rame / ça coupe" },
    { key: "other", icon: "question-circle", label: "Autre problème" },
]

/** Les problèmes du masteur : les mêmes clés, dits de sa place. */
const TUTOR_PROBLEMS: { key: UILiveProblem; icon: IconName; label: string }[] = [
    { key: "mic", icon: "microphone-slash", label: "Les élèves ne m'entendent pas" },
    { key: "cant_hear", icon: "volume-mute", label: "Je n'entends pas les élèves" },
    { key: "camera", icon: "video-slash", label: "Ma caméra ne marche pas" },
    { key: "connection", icon: "wifi", label: "Ça rame / ça coupe" },
    { key: "other", icon: "question-circle", label: "Autre problème" },
]

const CATEGORIES: { key: UILiveReportCategory; label: string }[] = [
    { key: "audio", label: "Audio" },
    { key: "video", label: "Vidéo" },
    { key: "connection", label: "Connexion" },
    { key: "screen_share", label: "Partage d'écran" },
    { key: "content", label: "Contenu / Énoncé" },
    { key: "other", label: "Autre" },
]

const TEXTS = {
    title: "Un problème ?",
    intro: "Qu'est-ce qui ne va pas ?",
    tryThis: "À essayer",
    stillStuck: "Toujours un problème ?",
    raiseHand: "Lever la main",
    openChat: "Écrire à ton masteur",
    report: "Signaler un problème",
    reportIntro: "Quel genre de problème ?",
    description: "Description",
    descriptionPlaceholder: "Décris le problème en quelques mots…",
    technical: "On joint les infos techniques pour t'aider.",
    send: "Envoyer le signalement",
    refresh: "Rafraîchir la page",
    openSettings: "Ouvrir les paramètres",
}

export function LiveHelpSheet(props: LiveHelpSheetProps) {
    const {
        visible,
        onClose,
        step,
        problem,
        onSelectProblem,
        onBack,
        diagnostic,
        onRaiseHand,
        onOpenChat,
        onReport,
        report,
        viewer = "student",
        onRefresh,
        onOpenSettings,
        testID,
    } = props
    const problems = viewer === "tutor" ? TUTOR_PROBLEMS : PROBLEMS

    const title =
        step === "report"
            ? TEXTS.report
            : step === "diagnostic"
              ? (problems.find((p) => p.key === problem)?.label ?? TEXTS.title)
              : TEXTS.title

    const footer =
        step === "report" ? (
            <Stack gap="2">
                {report.error != null && report.error !== "" && (
                    <Banner message={report.error} color="danger" icon="exclamation-circle" tinted align="start" />
                )}
                <Button
                    label={TEXTS.send}
                    onPress={report.onSend}
                    loading={report.sending}
                    disabled={report.category == null || report.description.trim() === ""}
                    fullWidth
                />
            </Stack>
        ) : undefined

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={title}
            steps={{ step: 1, total: 1, onBack: step === "choose" ? undefined : onBack }}
            sizeToContent
            footer={footer}
            testID={testID}
        >
            {step === "choose" && (
                <Stack gap="3">
                    <Text size="text-md" color="text-muted">
                        {TEXTS.intro}
                    </Text>
                    <CardList>
                        {problems.map((item) => (
                            <SettingRow
                                key={item.key}
                                iconName={item.icon}
                                label={item.label}
                                onPress={() => onSelectProblem(item.key)}
                            />
                        ))}
                    </CardList>
                </Stack>
            )}

            {step === "diagnostic" && (
                <Stack gap="4">
                    {diagnostic?.notice != null && <AlertCard severity="info" message={diagnostic.notice} />}
                    {diagnostic != null && (diagnostic.steps.length > 0 || (diagnostic.actions?.length ?? 0) > 0) && (
                        <Stack gap="2">
                            <SectionLabel>{TEXTS.tryThis}</SectionLabel>
                            <CardList>
                                {diagnostic.steps.map((item) => (
                                    <SettingRow
                                        key={item.text}
                                        iconName={item.iconName}
                                        label={item.text}
                                        description={item.detail}
                                        chevron={false}
                                    />
                                ))}
                                {diagnostic.actions?.map((item) => (
                                    <SettingRow
                                        key={`action-${item.label}`}
                                        iconName={item.iconName}
                                        label={item.label}
                                        description={item.detail}
                                        onPress={item.onPress}
                                    />
                                ))}
                            </CardList>
                        </Stack>
                    )}
                    <Stack gap="2">
                        <SectionLabel>{TEXTS.stillStuck}</SectionLabel>
                        <CardList>
                            {onRaiseHand != null && (
                                <SettingRow iconName="hand-paper" label={TEXTS.raiseHand} onPress={onRaiseHand} />
                            )}
                            {onOpenChat != null && (
                                <SettingRow iconName="comment" label={TEXTS.openChat} onPress={onOpenChat} />
                            )}
                            {onRefresh != null && (
                                <SettingRow iconName="sync-alt" label={TEXTS.refresh} onPress={onRefresh} />
                            )}
                            {onOpenSettings != null && (
                                <SettingRow iconName="cog" label={TEXTS.openSettings} onPress={onOpenSettings} />
                            )}
                            <SettingRow iconName="flag" label={TEXTS.report} onPress={onReport} />
                        </CardList>
                    </Stack>
                </Stack>
            )}

            {step === "report" && (
                <Stack gap="4">
                    <Text size="text-md" color="text-muted">
                        {TEXTS.reportIntro}
                    </Text>
                    <View>
                        <Row gap="2" style={styles.wrap}>
                            {CATEGORIES.map((item) => (
                                <ChoiceChip
                                    key={item.key}
                                    label={item.label}
                                    selected={report.category === item.key}
                                    onPress={() => report.onSelectCategory(item.key)}
                                />
                            ))}
                        </Row>
                    </View>
                    <TextInput
                        label={TEXTS.description}
                        value={report.description}
                        onChangeText={report.onChangeDescription}
                        placeholder={TEXTS.descriptionPlaceholder}
                        multiline
                        numberOfLines={3}
                        maxLength={1000}
                    />
                    {report.media != null && <MediaPicker {...report.media} />}
                    <Banner message={TEXTS.technical} icon="info-circle" color="info" align="start" tinted />
                </Stack>
            )}
        </Modal>
    )
}

const styles = StyleSheet.create(() => ({
    wrap: { flexWrap: "wrap" },
}))
